import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  useNavigate,
  Link,
  useLocation,
  useBeforeUnload,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Sparkles,
  Sparkle,
  Loader2,
  Cpu,
  FileText,
  ChevronDown,
  ShieldAlert,
  Wrench,
  Clock,
  Layers,
  AlertTriangle,
  Check,
  X,
  Sliders,
  BookOpen,
  CheckCircle2,
  Lightbulb,
  Zap,
  Info,
} from "lucide-react";
import { sopsAPI, machinesAPI, knowledgeAPI } from "../api";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import clsx from "clsx";

const CATEGORY_OPTIONS = [
  "Operation",
  "Maintenance",
  "Safety",
  "Troubleshooting",
];

const SKILL_LEVEL_OPTIONS = [
  { value: "Basic", label: "Basic (Standard Operator)" },
  { value: "Intermediate", label: "Intermediate (Trained Tech)" },
  { value: "Advanced", label: "Advanced (Certified Engineer)" },
  { value: "Expert", label: "Expert (OEM Specialist)" },
];

const QUICK_SUGGESTIONS = [
  {
    title: "Weekly Spindle Calibration & Runout Verification",
    purpose:
      "Verify precision spindle radial runout remains within 0.005mm tolerance and inspect dynamic balance after high-speed milling cycles.",
  },
  {
    title: "Emergency E-Stop Recovery & Axis Resync Protocol",
    purpose:
      "Clear controller fault codes, inspect servo axis positions, perform homing calibration, and re-engage safety interlocks after emergency halt.",
  },
  {
    title: "Hydraulic Fluid Inspection & Filter Element Replacement",
    purpose:
      "Drain reservoir sample for viscosity check, replace high-pressure filter cartridge, verify seal integrity, and purge air from main pump manifold.",
  },
  {
    title: "Chuck Collet Alignment & Clamping Force Diagnostic",
    purpose:
      "Disassemble collet assembly, inspect wear surfaces for galling, clean fine swarf, and calibrate pneumatic/hydraulic clamping pressure to OEM specs.",
  },
];

const GENERATION_STAGES = [
  "Retrieving approved OEM technical manual...",
  "Analyzing machine tolerances, schematics & specs...",
  "Synthesizing sequential step instructions...",
  "Extracting safety precautions & PPE protocols...",
  "Formulating required tooling matrix & checkpoints...",
  "Finalizing verified procedure draft...",
];

// Custom dropdown component matching design system with configurable accent color
const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  loading,
  disabled,
  renderOption,
  className,
  accentColor = "blue",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getOptionValue = (opt) => {
    if (opt === null || opt === undefined) return "";
    if (typeof opt === "object") {
      return opt.value !== undefined ? opt.value : (opt.id || opt._id || "");
    }
    return opt;
  };

  const getOptionLabel = (opt) => {
    if (opt === null || opt === undefined) return "";
    if (renderOption) return renderOption(opt);
    if (typeof opt === "object") {
      return opt.label || opt.name || opt.title || opt.id || "";
    }
    return String(opt);
  };

  const selectedOption = options?.find((opt) => getOptionValue(opt) === value);
  const isOrange = accentColor === "orange";

  return (
    <div className={clsx("relative", className)} ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          "w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 flex justify-between items-center transition-all shadow-xs text-left",
          disabled
            ? "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
            : isOpen
              ? isOrange
                ? "border-[#E38A29] ring-2 ring-[#E38A29]/20"
                : "border-blue-500 ring-2 ring-blue-500/20"
              : "border-slate-200 hover:border-slate-300"
        )}
      >
        <div className="flex items-center min-w-0 flex-1">
          <span
            className={clsx(
              !selectedOption && "text-slate-400",
              "truncate font-medium text-slate-800"
            )}
          >
            {selectedOption ? getOptionLabel(selectedOption) : placeholder}
          </span>
        </div>
        <ChevronDown
          className={clsx(
            "w-4 h-4 text-slate-400 transition-transform duration-200 flex-shrink-0 ml-2",
            isOpen && (isOrange ? "rotate-180 text-[#E38A29]" : "rotate-180 text-blue-600")
          )}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.99 }}
            transition={{ duration: 0.15 }}
            className="absolute z-30 mt-1.5 w-full bg-white rounded-xl shadow-lg border border-slate-200/90 py-1.5 max-h-60 overflow-y-auto"
          >
            {loading && (
              <div className="px-3.5 py-3 text-xs text-slate-500 flex items-center justify-center gap-2">
                <Loader2
                  className={clsx(
                    "w-3.5 h-3.5 animate-spin",
                    isOrange ? "text-[#E38A29]" : "text-blue-600"
                  )}
                />
                <span>Loading options...</span>
              </div>
            )}
            {!loading && (!options || options.length === 0) && (
              <div className="px-3.5 py-3 text-xs text-slate-400 text-center">
                No options available
              </div>
            )}
            {!loading &&
              options?.map((option, idx) => {
                const optVal = getOptionValue(option);
                const isSelected = optVal === value;
                return (
                  <button
                    type="button"
                    key={optVal || idx}
                    onClick={() => {
                      onChange(optVal);
                      setIsOpen(false);
                    }}
                    className={clsx(
                      "w-[calc(100%-8px)] mx-1 px-3 py-2 text-xs sm:text-sm text-left transition-colors flex items-center justify-between font-medium rounded-lg",
                      isSelected
                        ? isOrange
                          ? "bg-[#FFE9D0] text-[#b8620c] font-semibold"
                          : "bg-blue-50 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <span className="truncate">{getOptionLabel(option)}</span>
                    {isSelected && (
                      <Check
                        className={clsx(
                          "w-4 h-4 flex-shrink-0 ml-2",
                          isOrange ? "text-[#E38A29]" : "text-blue-600"
                        )}
                      />
                    )}
                  </button>
                );
              })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CreateSOP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [isAiMode, setIsAiMode] = useState(false);
  const [isReviewingDraft, setIsReviewingDraft] = useState(false);
  const [aiInputs, setAiInputs] = useState({ title: "", purpose: "" });
  const [selectedDoc, setSelectedDoc] = useState("");
  const [selectedMachine, setSelectedMachine] = useState("");
  const [generationStageIndex, setGenerationStageIndex] = useState(0);

  const [formData, setFormData] = useState({
    machineId: "",
    title: "",
    description: "",
    category: "Operation",
    estimatedDuration: 30,
    skillLevel: "Intermediate",
    steps: [
      {
        order: 1,
        instruction: "",
        warning: "",
        checkPoint: false,
        requiresImage: false,
      },
    ],
    safetyWarnings: [""],
    requiredTools: [""],
    tags: "",
  });

  const generateMutation = useMutation({
    mutationFn: (data) => sopsAPI.generate(data),
    onSuccess: (res) => {
      const generated = res.data.data;
      setFormData((prev) => ({
        ...prev,
        title: generated.title || aiInputs.title,
        description: generated.description || aiInputs.purpose,
        steps:
          Array.isArray(generated.steps) && generated.steps.length > 0
            ? generated.steps
            : prev.steps,
        safetyWarnings:
          Array.isArray(generated.safety_warnings) &&
            generated.safety_warnings.length > 0
            ? generated.safety_warnings
            : prev.safetyWarnings,
        requiredTools:
          Array.isArray(generated.required_tools) &&
            generated.required_tools.length > 0
            ? generated.required_tools
            : prev.requiredTools,
        estimatedDuration:
          generated.estimated_duration || prev.estimatedDuration,
        machineId: selectedMachine,
      }));
      setIsAiMode(false);
      setIsReviewingDraft(true);
      toast.success("AI Draft Generated! Review and edit details below.");
    },
    onError: (err) =>
      toast.error(
        "AI failed to generate: " + (err.response?.data?.error || err.message),
      ),
  });

  const isGenerating = generateMutation.isPending;

  // Cycle generation stages animation
  useEffect(() => {
    let interval;
    if (isGenerating) {
      setGenerationStageIndex(0);
      interval = setInterval(() => {
        setGenerationStageIndex((prev) => (prev + 1) % GENERATION_STAGES.length);
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [isGenerating]);

  useBeforeUnload(
    useCallback(
      (event) => {
        if (isGenerating) {
          event.preventDefault();
          return (event.returnValue = "");
        }
      },
      [isGenerating],
    ),
  );

  useEffect(() => {
    if (location.state?.initialData) {
      const data = location.state.initialData;
      setIsReviewingDraft(true);
      setFormData((prev) => ({
        ...prev,
        machineId: data.machineId || prev.machineId,
        title: data.title || prev.title,
        description: data.description || prev.description,
        estimatedDuration: data.estimated_duration || prev.estimatedDuration,
        requiredTools:
          Array.isArray(data.required_tools) && data.required_tools.length > 0
            ? data.required_tools
            : prev.requiredTools,
        safetyWarnings:
          Array.isArray(data.safety_warnings) &&
            data.safety_warnings.length > 0
            ? data.safety_warnings
            : prev.safetyWarnings,
        steps:
          data.steps?.map((s, i) => ({
            order: i + 1,
            instruction: s.instruction || "",
            warning: s.warning || "",
            checkPoint: !!s.checkPoint,
            requiresImage: !!s.requiresImage,
          })) || prev.steps,
      }));
      toast.success("SOP drafted from AI Assistant");
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const { data: machines } = useQuery({
    queryKey: ["machines-list"],
    queryFn: () =>
      machinesAPI.getAll({ limit: 100 }).then((res) => res.data.data),
  });

  // Filtered Knowledge Documents based on selected Target Machine
  const { data: documents } = useQuery({
    queryKey: ["knowledge-docs", selectedMachine],
    queryFn: () =>
      knowledgeAPI
        .getAll({
          status: "approved",
          machineId: selectedMachine,
        })
        .then((res) => res.data.data),
    enabled: isAiMode && !!selectedMachine,
  });

  const createMutation = useMutation({
    mutationFn: (data) => sopsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sops"] });
      toast.success("SOP created successfully");
      navigate("/sops");
    },
    onError: (err) =>
      toast.error(err.response?.data?.error || "Failed to create SOP"),
  });

  const handleGenerate = () => {
    if (
      !selectedDoc ||
      !selectedMachine ||
      !aiInputs.title ||
      !aiInputs.purpose
    ) {
      toast.error("Please fill in all required fields (Title, Purpose, Machine, Document)");
      return;
    }
    generateMutation.mutate({
      documentId: selectedDoc,
      machineId: selectedMachine,
      title: aiInputs.title,
      purpose: aiInputs.purpose,
      category: formData.category,
      skillLevel: formData.skillLevel,
      estimatedDuration: formData.estimatedDuration,
    });
  };

  const handleSelectSuggestion = (sugg) => {
    setAiInputs({
      title: sugg.title,
      purpose: sugg.purpose,
    });
    toast.success("Applied suggestion template");
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!formData.title?.trim()) {
      toast.error("SOP Title is required");
      return;
    }
    if (!formData.machineId) {
      toast.error("Please select a target machine");
      return;
    }
    const data = {
      ...formData,
      tags:
        typeof formData.tags === "string"
          ? formData.tags
            .split(",")
            .map((t) => t.trim())
            .filter((t) => t)
          : [],
      steps: formData.steps.map((s, i) => ({ ...s, order: i + 1 })),
    };
    createMutation.mutate(data);
  };

  const countWords = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const handleDescriptionChange = (value) => {
    const words = value.trim().split(/\s+/).filter(Boolean);
    if (words.length > 500) {
      const trimmed = words.slice(0, 500).join(" ");
      setFormData({ ...formData, description: trimmed });
      toast.error("Description limited to 500 words");
    } else {
      setFormData({ ...formData, description: value });
    }
  };

  const addStep = () =>
    setFormData({
      ...formData,
      steps: [
        ...formData.steps,
        {
          order: formData.steps.length + 1,
          instruction: "",
          warning: "",
          checkPoint: false,
          requiresImage: false,
        },
      ],
    });

  const removeStep = (index) =>
    setFormData({
      ...formData,
      steps:
        formData.steps.length > 1
          ? formData.steps.filter((_, i) => i !== index)
          : formData.steps,
    });

  const updateStep = (index, field, value) => {
    const newSteps = [...formData.steps];
    newSteps[index][field] = value;
    setFormData({ ...formData, steps: newSteps });
  };

  const handleListChange = (field, index, value) => {
    const newList = [...formData[field]];
    newList[index] = value;
    setFormData({ ...formData, [field]: newList });
  };

  const addListItem = (field) =>
    setFormData({ ...formData, [field]: [...formData[field], ""] });

  const removeListItem = (field, index) =>
    setFormData({
      ...formData,
      [field]:
        formData[field].length > 1
          ? formData[field].filter((_, i) => i !== index)
          : formData[field],
    });

  // AI Readiness state
  const isTitleFilled = Boolean(aiInputs.title?.trim());
  const isPurposeFilled = Boolean(aiInputs.purpose?.trim());
  const isMachineSelected = Boolean(selectedMachine);
  const isDocSelected = Boolean(selectedDoc);
  const isAiReady =
    isTitleFilled && isPurposeFilled && isMachineSelected && isDocSelected;

  const selectedMachineObj = machines?.find(
    (m) => (m.id || m._id) === selectedMachine,
  );

  return (
    <div className="space-y-6 lg:space-y-7">
      {/* Top Header matching Dashboard & Machines */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-3 border-b border-slate-200/90">
        <div className="flex items-center gap-3.5 min-w-0">
          <Link
            to="/sops"
            className="p-2.5 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 border border-slate-200/90 rounded-xl shadow-xs transition-all shrink-0 hover:shadow-sm active:scale-95"
            title="Back to procedures list"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight truncate">
                {isReviewingDraft ? "Review AI Draft" : "Create Operating Procedure"}
              </h1>
              {isReviewingDraft && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 shrink-0">
                  <Sparkles className="w-3 h-3 text-indigo-600" />
                  AI Synthesized
                </span>
              )}
            </div>

            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Standardized operational guidelines, checklists & equipment protocols
            </p>
          </div>
        </div>

        {/* AI Mode On/Off Toggle */}
        {!isReviewingDraft && (
          <div className="flex items-center self-start sm:self-center select-none">
            <div className="inline-flex items-center gap-2">
              <span
                className={clsx(
                  "font-bold text-xs sm:text-sm transition-colors",
                  isAiMode ? "text-[#b8620c]" : "text-slate-600"
                )}
              >
                AI Mode
              </span>

              {/* Only this toggle button is clickable */}
              <button
                type="button"
                role="switch"
                aria-checked={isAiMode}
                onClick={() => setIsAiMode(!isAiMode)}
                className={clsx(
                  "relative w-9 h-5 rounded-full transition-colors duration-200 p-0.5 flex items-center cursor-pointer outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0 ml-1 select-none",
                  isAiMode ? "bg-[#E38A29]" : "bg-slate-300 hover:bg-slate-400/80"
                )}
                title={isAiMode ? "Click to deactivate AI Mode" : "Click to activate AI Mode"}
              >
                <motion.div
                  animate={{ x: isAiMode ? 16 : 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  className="w-4 h-4 rounded-full bg-white shadow-xs pointer-events-none flex items-center justify-center"
                >
                  <Sparkle
                    className={clsx(
                      "w-2.5 h-2.5 transition-all rotate-[30deg]",
                      isAiMode ? "text-[#E38A29] fill-[#E38A29]" : "text-slate-400 fill-slate-400"
                    )}
                  />
                </motion.div>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area with Fluid Transitions */}
      <AnimatePresence mode="wait">
        {isAiMode ? (
          /* ============================================================ */
          /*  AI ASSISTED SYNTHESIS VIEW (ORANGE ACCENTED INDUSTRIAL UI)  */
          /* ============================================================ */
          <motion.div
            key="ai-mode-container"
            initial={{ opacity: 0, y: 16, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.99 }}
            transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white border border-slate-200/90 rounded-2xl shadow-sm p-6 sm:p-8 space-y-6"
          >
            {/* Ambient AI Header with Brand Orange Palette */}
            <div className="flex items-start gap-4 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-[#FFE9D0]/75 via-[#FFE9D0]/35 to-orange-50/25 border border-[#E38A29]/30">
              <div className="w-11 h-11 rounded-xl bg-[#E38A29] text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-[#E38A29]/25">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-bold text-slate-900 text-sm sm:text-base">
                    ConvoLink AI Procedure Synthesis
                  </h3>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-white text-[#b8620c] border border-[#E38A29]/35 shadow-2xs">
                    <Zap className="w-3 h-3 text-[#E38A29]" />
                    Grounded in OEM Manuals
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Select your target machinery and approved technical document.
                  ConvoLink AI extracts operational steps, safety hazards, PPE requirements,
                  and verification checkpoints directly from OEM manuals.
                </p>
              </div>
            </div>

            {/* Section 1: Equipment & Documentation Grounding */}
            <div className="space-y-4 pt-1">
              <div className="flex items-center gap-2.5 pb-2.5 border-b border-slate-100">
                <div className="w-8 h-8 rounded-lg bg-[#FFE9D0] text-[#b8620c] flex items-center justify-center">
                  <Cpu className="w-4 h-4 text-[#E38A29]" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">
                    Equipment & Reference Manual
                  </h3>
                  <p className="text-xs text-slate-500">
                    Link procedure generation to an approved machine and its technical documentation
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Target Machine <span className="text-rose-500">*</span>
                  </label>
                  <CustomDropdown
                    options={machines}
                    value={selectedMachine}
                    onChange={(val) => {
                      setSelectedMachine(val);
                      setSelectedDoc("");
                    }}
                    placeholder="Select target machinery..."
                    loading={!machines}
                    accentColor="orange"
                    renderOption={(m) =>
                      `${m.name} (${m.model || m.serial_number || "Machine"})`
                    }
                  />
                  {selectedMachineObj && (
                    <div className="flex items-center gap-2 text-[11px] text-slate-500 pt-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span>Status: {selectedMachineObj.status || "Operational"}</span>
                      <span className="text-slate-300">•</span>
                      <span>Type: {selectedMachineObj.type || "Industrial Machine"}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Approved OEM Manual <span className="text-rose-500">*</span>
                  </label>
                  <CustomDropdown
                    options={documents}
                    value={selectedDoc}
                    onChange={setSelectedDoc}
                    placeholder={
                      selectedMachine
                        ? "Select documentation manual..."
                        : "Select machine first..."
                    }
                    loading={!documents && !!selectedMachine}
                    disabled={!selectedMachine}
                    accentColor="orange"
                    renderOption={(d) => d.title}
                  />
                  {selectedMachine && documents && documents.length > 0 && (
                    <div className="flex items-center gap-1.5 text-[11px] text-[#b8620c] pt-1.5 font-medium">
                      <BookOpen className="w-3.5 h-3.5 text-[#E38A29]" />
                      <span>{documents.length} approved technical document(s) available</span>
                    </div>
                  )}
                  {selectedMachine && documents && documents.length === 0 && (
                    <div className="text-[11px] text-amber-600 pt-1.5">
                      No approved manuals found for this machine. Upload one in Knowledge Base.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section 2: Procedure Objective & Context */}
            <div className="space-y-4 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2.5 pb-2.5 border-b border-slate-100">
                <div className="w-8 h-8 rounded-lg bg-[#FFE9D0] text-[#b8620c] flex items-center justify-center">
                  <FileText className="w-4 h-4 text-[#E38A29]" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">
                    Procedure Objective & Context
                  </h3>
                  <p className="text-xs text-slate-500">
                    Define the operational goal and instructions for AI synthesis
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Procedure Title <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#E38A29]/25 focus:border-[#E38A29] shadow-xs transition-all"
                  placeholder="e.g. Weekly Spindle Calibration & Runout Verification"
                  value={aiInputs.title}
                  onChange={(e) =>
                    setAiInputs({ ...aiInputs, title: e.target.value })
                  }
                />
              </div>

              {/* Quick Suggestion Templates */}
              <div className="space-y-2 pt-1">
                <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                  <Lightbulb className="w-3.5 h-3.5 text-[#E38A29]" />
                  <span>Quick Templates (Click to fill):</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {QUICK_SUGGESTIONS.map((sugg, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleSelectSuggestion(sugg)}
                      className="px-2.5 py-1.5 rounded-lg bg-slate-50 hover:bg-[#FFE9D0]/70 border border-slate-200 hover:border-[#E38A29]/40 text-slate-700 hover:text-[#b8620c] text-xs font-medium transition-all text-left shadow-2xs hover:shadow-xs active:scale-[0.98]"
                    >
                      + {sugg.title.split("&")[0].trim()}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Goal & Purpose Statement <span className="text-rose-500">*</span>
                  </label>
                  <span className="text-xs text-slate-400">
                    {countWords(aiInputs.purpose)} words
                  </span>
                </div>
                <textarea
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#E38A29]/25 focus:border-[#E38A29] shadow-xs transition-all min-h-[90px] resize-y"
                  placeholder="e.g. Ensure precision spindle runout remains within 0.005mm tolerance after high-speed milling cycles. Check bearing temperature and calibrate lubrication flow..."
                  value={aiInputs.purpose}
                  onChange={(e) =>
                    setAiInputs({ ...aiInputs, purpose: e.target.value })
                  }
                />
                <div className="mt-2 p-2.5 rounded-xl bg-[#FFE9D0]/50 border border-[#E38A29]/25 flex items-start gap-2 text-[11px] text-slate-700 leading-normal">
                  <Info className="w-3.5 h-3.5 text-[#E38A29] mt-0.5 shrink-0" />
                  <span>
                    Mention specific tolerances, target components, or symptoms. The AI uses these details to generate exact verification checkpoints.
                  </span>
                </div>
              </div>
            </div>

            {/* Section 3: Synthesis Preferences (Tuning) */}
            <div className="space-y-4 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2.5 pb-2.5 border-b border-slate-100">
                <div className="w-8 h-8 rounded-lg bg-[#FFE9D0] text-[#b8620c] flex items-center justify-center">
                  <Sliders className="w-4 h-4 text-[#E38A29]" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">
                    Procedure Classification & Tuning
                  </h3>
                  <p className="text-xs text-slate-500">
                    Optional specifications for duration and technician qualification level
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Category
                  </label>
                  <CustomDropdown
                    options={CATEGORY_OPTIONS}
                    value={formData.category}
                    onChange={(val) =>
                      setFormData({ ...formData, category: val })
                    }
                    placeholder="Select category..."
                    accentColor="orange"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Est. Duration (min)
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#E38A29]/25 focus:border-[#E38A29] shadow-xs transition-all"
                    value={formData.estimatedDuration}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        estimatedDuration: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Skill Level
                  </label>
                  <CustomDropdown
                    options={SKILL_LEVEL_OPTIONS}
                    value={formData.skillLevel}
                    onChange={(val) =>
                      setFormData({ ...formData, skillLevel: val })
                    }
                    placeholder="Select skill level..."
                    accentColor="orange"
                  />
                </div>
              </div>
            </div>

            {/* Form Actions Footer */}
            <div className="flex items-center justify-end gap-3 pt-5 border-t border-slate-200/80">
              <Link to="/sops">
                <button
                  type="button"
                  className="btn btn-secondary text-xs sm:text-sm py-2 px-4 shadow-xs"
                >
                  Cancel
                </button>
              </Link>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating || !isAiReady}
                className="px-5 py-2.5 rounded-xl font-semibold text-xs sm:text-sm text-white shadow-md shadow-[#E38A29]/25 hover:shadow-lg hover:shadow-[#E38A29]/35 bg-[#E38A29] hover:bg-[#d47a19] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all active:scale-[0.98]"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                    <span>Synthesizing SOP...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 text-white" />
                    <span>Generate SOP Draft</span>
                  </>
                )}
              </button>
            </div>

            {/* In-Flight Generation Modal / Overlay */}
            <AnimatePresence>
              {isGenerating && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4"
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 10 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 10 }}
                    className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl border border-slate-100 text-center space-y-6"
                  >
                    {/* Glowing AI Orb in Brand Orange */}
                    <div className="relative mx-auto w-20 h-20 flex items-center justify-center">
                      <div className="absolute inset-0 rounded-full bg-[#E38A29] blur-xl opacity-40 animate-pulse" />
                      <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-tr from-[#E38A29] via-[#f19d44] to-[#d67b1c] text-white flex items-center justify-center shadow-lg shadow-[#E38A29]/30">
                        <Sparkles className="w-8 h-8 animate-spin" style={{ animationDuration: "5s" }} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-lg font-bold text-slate-900">
                        Synthesizing Standard Procedure
                      </h3>
                      <p className="text-xs text-slate-500 max-w-xs mx-auto">
                        ConvoLink AI is grounding technical steps with OEM specifications from approved documentation.
                      </p>
                    </div>

                    {/* Live Stage Cycler */}
                    <div className="p-3.5 rounded-2xl bg-[#FFE9D0]/70 border border-[#E38A29]/30 text-[#b8620c] flex items-center justify-center gap-2.5 text-xs font-semibold">
                      <Loader2 className="w-4 h-4 animate-spin text-[#E38A29] shrink-0" />
                      <span className="truncate">
                        {GENERATION_STAGES[generationStageIndex]}
                      </span>
                    </div>

                    <p className="text-[11px] text-slate-400">
                      This process typically takes 10–20 seconds. Please keep this tab open.
                    </p>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ) : (
          /* ============================================================ */
          /*  MANUAL PROCEDURE CONFIGURATION VIEW                        */
          /* ============================================================ */
          <motion.form
            key="manual-mode-container"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 16, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.99 }}
            transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
            className="bg-white border border-slate-200/90 rounded-2xl shadow-sm p-6 sm:p-8 space-y-6"
          >
            {/* Ambient Manual Form Header */}
            <div className="flex items-start gap-3.5 p-4 rounded-xl bg-gradient-to-r from-blue-50/80 via-slate-50/50 to-indigo-50/80 border border-blue-100">
              <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-blue-500/20">
                <FileText className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-slate-900 text-sm sm:text-base">
                  Standard Operating Procedure Configuration
                </h3>
                <p className="text-xs text-slate-600 mt-0.5">
                  Define equipment specifications, safety protocols, required tooling, and sequential execution steps.
                </p>
              </div>
            </div>

            {/* Section 1: Procedure Overview */}
            <div className="space-y-4 pt-1">
              <div className="flex items-center gap-2.5 pb-2.5 border-b border-slate-100">
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">
                    Procedure Overview
                  </h3>
                  <p className="text-xs text-slate-500">
                    Specify title, target equipment, classification and scope
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    SOP Title <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-xs transition-all"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="e.g. Daily Spindle Warmup & Pre-Shift Check"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Target Machine <span className="text-rose-500">*</span>
                  </label>
                  <CustomDropdown
                    options={machines}
                    value={formData.machineId}
                    onChange={(val) =>
                      setFormData({ ...formData, machineId: val })
                    }
                    placeholder="Select target machinery..."
                    loading={!machines}
                    accentColor="blue"
                    renderOption={(m) =>
                      `${m.name} (${m.model || m.serial_number || "Machine"})`
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Category
                  </label>
                  <CustomDropdown
                    options={CATEGORY_OPTIONS}
                    value={formData.category}
                    onChange={(val) =>
                      setFormData({ ...formData, category: val })
                    }
                    placeholder="Select category..."
                    accentColor="blue"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Est. Duration (min)
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-xs transition-all"
                    value={formData.estimatedDuration}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        estimatedDuration: parseInt(e.target.value) || 0,
                      })
                    }
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Skill Level
                  </label>
                  <CustomDropdown
                    options={SKILL_LEVEL_OPTIONS}
                    value={formData.skillLevel}
                    onChange={(val) =>
                      setFormData({ ...formData, skillLevel: val })
                    }
                    placeholder="Select skill level..."
                    accentColor="blue"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Description
                  </label>
                  <span className="text-xs text-slate-400">
                    {countWords(formData.description)} / 500 words
                  </span>
                </div>
                <textarea
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-xs transition-all min-h-[90px] resize-y"
                  value={formData.description}
                  placeholder="Provide an overview of this procedure, scope, conditions, and intended outcome..."
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                />
              </div>
            </div>

            {/* Section 2: Safety Warnings & Required Tools */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-3 border-t border-slate-100">
              {/* Safety Warnings Panel */}
              <div className="p-4 sm:p-5 bg-slate-50/70 border border-slate-200/90 rounded-2xl space-y-3.5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between pb-2.5 border-b border-slate-200/80 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
                        <ShieldAlert className="w-4 h-4" />
                      </div>
                      <h3 className="font-bold text-slate-900 text-xs sm:text-sm">
                        Safety Warnings & Hazards
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => addListItem("safetyWarnings")}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-700 px-2 py-1 rounded-lg hover:bg-rose-50 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Add</span>
                    </button>
                  </div>

                  <div className="space-y-2.5">
                    {(formData.safetyWarnings || []).map((sw, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400 shadow-xs transition-all"
                          placeholder={`Hazard #${i + 1} (e.g. Ensure lockout tagout is active)`}
                          value={sw}
                          onChange={(e) =>
                            handleListChange("safetyWarnings", i, e.target.value)
                          }
                        />
                        {formData.safetyWarnings.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeListItem("safetyWarnings", i)}
                            className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors flex-shrink-0"
                            title="Remove hazard warning"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Required Tools Panel */}
              <div className="p-4 sm:p-5 bg-slate-50/70 border border-slate-200/90 rounded-2xl space-y-3.5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between pb-2.5 border-b border-slate-200/80 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <Wrench className="w-4 h-4" />
                      </div>
                      <h3 className="font-bold text-slate-900 text-xs sm:text-sm">
                        Required Tools & PPE
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => addListItem("requiredTools")}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Add</span>
                    </button>
                  </div>

                  <div className="space-y-2.5">
                    {(formData.requiredTools || []).map((rt, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 shadow-xs transition-all"
                          placeholder={`Tool #${i + 1} (e.g. 10mm Torque Wrench, Safety Goggles)`}
                          value={rt}
                          onChange={(e) =>
                            handleListChange("requiredTools", i, e.target.value)
                          }
                        />
                        {formData.requiredTools.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeListItem("requiredTools", i)}
                            className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors flex-shrink-0"
                            title="Remove tool"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Step-by-Step Procedure */}
            <div className="space-y-4 pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between pb-2.5 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <Layers className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-slate-900">
                      Procedure Steps
                    </h3>
                    <p className="text-xs text-slate-500">
                      Sequential action steps, safety precautions, and quality checkpoints
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addStep}
                  className="btn btn-secondary text-xs py-1.5 px-3 rounded-xl inline-flex items-center gap-1.5 shadow-xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add Step</span>
                </button>
              </div>

              <div className="space-y-4">
                {(formData.steps || []).map((step, i) => (
                  <div
                    key={i}
                    className="p-4 bg-slate-50/70 border border-slate-200/90 rounded-2xl space-y-3 relative group transition-all hover:bg-slate-50 hover:border-slate-300"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-600 text-white shadow-xs">
                          Step {i + 1}
                        </span>
                        {step.checkPoint && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 border border-emerald-200">
                            Checkpoint
                          </span>
                        )}
                        {step.requiresImage && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-blue-100 text-blue-800 border border-blue-200">
                            Photo Required
                          </span>
                        )}
                      </div>
                      {formData.steps.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeStep(i)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors opacity-80 group-hover:opacity-100"
                          title="Remove this step"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    <textarea
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-xs transition-all resize-y min-h-[65px]"
                      placeholder={`Enter step ${i + 1} instruction...`}
                      value={step.instruction}
                      onChange={(e) =>
                        updateStep(i, "instruction", e.target.value)
                      }
                      rows="2"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
                      <div className="sm:col-span-7">
                        <div className="relative">
                          <AlertTriangle className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-amber-500" />
                          <input
                            className="w-full pl-8 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 shadow-xs transition-all"
                            placeholder="Optional caution or safety warning..."
                            value={step.warning}
                            onChange={(e) =>
                              updateStep(i, "warning", e.target.value)
                            }
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-5 flex items-center justify-start sm:justify-end gap-3 flex-wrap">
                        <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 cursor-pointer bg-white px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors shadow-2xs">
                          <input
                            type="checkbox"
                            checked={step.checkPoint}
                            onChange={(e) =>
                              updateStep(i, "checkPoint", e.target.checked)
                            }
                            className="w-3.5 h-3.5 rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span>Checkpoint</span>
                        </label>

                        <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 cursor-pointer bg-white px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors shadow-2xs">
                          <input
                            type="checkbox"
                            checked={step.requiresImage}
                            onChange={(e) =>
                              updateStep(i, "requiresImage", e.target.checked)
                            }
                            className="w-3.5 h-3.5 rounded text-blue-600 focus:ring-blue-500"
                          />
                          <span>Requires Image</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Actions Footer */}
            <div className="flex items-center justify-end gap-3 pt-5 border-t border-slate-200/80">
              <Link to="/sops">
                <button
                  type="button"
                  className="btn btn-secondary text-xs sm:text-sm py-2 px-4 shadow-xs"
                >
                  Cancel
                </button>
              </Link>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={createMutation.isPending}
                className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 whitespace-nowrap flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save SOP</span>
                  </>
                )}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreateSOP;
